export const CHART_BG = '#0c0e12';
export const CHART_DOT = '#13161B';
export const CHART_AXIS = '#ffffff';
export const CHART_BAR = '#4ea8ff';
export const CHART_HIST = '#3dbe72';
export const CHART_SCATTER = '#4ea8ff';
export const CHART_DIAGONAL = 'rgba(255,255,255,0.35)';

export type PlotRect = { left: number; top: number; right: number; bottom: number };

export const defaultPlot = (width: number, height: number): PlotRect => ({
    left: 48,
    top: 14,
    right: width - 12,
    bottom: height - 40,
});

export const setupCanvas = (
    canvas: HTMLCanvasElement,
    cssWidth: number,
    cssHeight: number,
): CanvasRenderingContext2D | null => {
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.floor(cssWidth * dpr));
    canvas.height = Math.max(1, Math.floor(cssHeight * dpr));
    canvas.style.width = `${cssWidth}px`;
    canvas.style.height = `${cssHeight}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    return ctx;
};

export const drawBackground = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
    ctx.fillStyle = CHART_BG;
    ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = CHART_DOT;
    for (let x = 15; x < width; x += 15) {
        for (let y = 15; y < height; y += 15) {
            ctx.fillRect(x, y, 2, 2);
        }
    }
};

export const linMap = (v: number, a: number, b: number, c: number, d: number) => {
    const span = b - a || 1;
    return c + ((v - a) / span) * (d - c);
};

export const formatTick = (v: number) => {
    if (!Number.isFinite(v)) return '0';
    if (Math.abs(v) >= 100) return v.toFixed(0);
    if (Math.abs(v) >= 10) return v.toFixed(1);
    return v.toFixed(2);
};

export const niceStep = (span: number, maxTicks: number) => {
    const raw = span / Math.max(maxTicks, 1);
    if (!(raw > 0) || !Number.isFinite(raw)) return 1;
    const mag = Math.pow(10, Math.floor(Math.log10(raw)));
    const r = raw / mag;
    const nice = r <= 1 ? 1 : r <= 2 ? 2 : r <= 5 ? 5 : 10;
    return nice * mag;
};

export const drawAxisFrame = (
    ctx: CanvasRenderingContext2D,
    plot: PlotRect,
    yLabel: string,
    yMin: number,
    yMax: number,
) => {
    ctx.strokeStyle = CHART_AXIS;
    ctx.fillStyle = CHART_AXIS;
    ctx.lineWidth = 1;
    ctx.font = '11px Arial';
    ctx.beginPath();
    ctx.moveTo(plot.left, plot.top);
    ctx.lineTo(plot.left, plot.bottom);
    ctx.lineTo(plot.right, plot.bottom);
    ctx.stroke();

    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    ctx.fillText(formatTick(yMin), plot.left - 6, plot.bottom);
    ctx.fillText(formatTick(yMax), plot.left - 6, plot.top);
    ctx.save();
    ctx.translate(12, (plot.top + plot.bottom) / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.textAlign = 'center';
    ctx.fillText(yLabel, 0, 0);
    ctx.restore();
};

export const drawLinearXTicks = (
    ctx: CanvasRenderingContext2D,
    plot: PlotRect,
    xMin: number,
    xMax: number,
    xLabel: string,
) => {
    const plotW = Math.max(plot.right - plot.left, 1);
    const maxTicks = Math.max(2, Math.floor(plotW / 72));
    const step = niceStep(xMax - xMin, maxTicks);
    const start = Math.ceil(xMin / step) * step;

    ctx.strokeStyle = CHART_AXIS;
    ctx.fillStyle = CHART_AXIS;
    ctx.lineWidth = 1;
    ctx.font = '11px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';

    for (let v = start; v <= xMax + step * 1e-6; v += step) {
        const px = linMap(v, xMin, xMax, plot.left, plot.right);
        ctx.beginPath();
        ctx.moveTo(px, plot.bottom);
        ctx.lineTo(px, plot.bottom + 5);
        ctx.stroke();
        ctx.fillText(formatTick(v), px, plot.bottom + 6);
    }

    ctx.fillText(xLabel, (plot.left + plot.right) / 2, plot.bottom + 20);
};

export const drawAxes = (
    ctx: CanvasRenderingContext2D,
    plot: PlotRect,
    xLabel: string,
    yLabel: string,
    xMin: number,
    xMax: number,
    yMin: number,
    yMax: number,
) => {
    drawAxisFrame(ctx, plot, yLabel, yMin, yMax);
    drawLinearXTicks(ctx, plot, xMin, xMax, xLabel);
};
